import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  ArrowRightLeft,
  Search,
  GitBranch,
  Key,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Inbox,
} from 'lucide-react';
import { systemApi } from '../services/api';
import { formatCompactNumber, formatRelativeTime } from '../utils/helpers';

// ---------------------------------------------------------------------------
// Status colour maps
// ---------------------------------------------------------------------------

const TRANSFER_STATUS_COLORS = {
  COMMITTED: 'bg-success-100 text-success-700',
  RESERVED:  'bg-blue-100   text-blue-700',
  ABORTED:   'bg-danger-100 text-danger-700',
  EXPIRED:   'bg-warning-100 text-warning-700',
  PENDING:   'bg-gray-100   text-gray-600',
  ERROR:     'bg-danger-100 text-danger-700',
};

const PAYEE_STATUS_COLORS = {
  RECEIVED:     'bg-blue-100    text-blue-700',
  CBS_NOTIFIED: 'bg-warning-100 text-warning-700',
  RESPONDED:    'bg-success-100 text-success-700',
  FAILED:       'bg-danger-100  text-danger-700',
  ABORTED:      'bg-gray-100    text-gray-600',
};

const REQUEST_TYPE_LABELS = {
  PARTY_LOOKUP:   'Lookup',
  QUOTE:          'Quote',
  TRANSFER:       'Transfer',
  TRANSFER_ERROR: 'Tx Error',
};

function StatusPill({ status, colorMap }) {
  const cls = (colorMap && colorMap[status]) || 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {status ?? '—'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Progress bar row
// ---------------------------------------------------------------------------

function BreakdownRow({ label, value, total, colorClass }) {
  const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-gray-700">{label}</span>
        <span className="font-medium text-gray-900">
          {value}{' '}
          <span className="text-gray-400 font-normal">({pct}%)</span>
        </span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-1.5">
        <div
          className={`${colorClass} h-1.5 rounded-full transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

const Dashboard = () => {
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const loadData = useCallback(async () => {
    try {
      setError(null);
      const data = await systemApi.getMojaloopStats();
      console.log('[Dashboard] getMojaloopStats response:', JSON.stringify(data, null, 2));
      setStats(data);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
          <p className="text-gray-600">Loading dashboard…</p>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
          <p className="text-gray-900 font-medium mb-2">Error Loading Dashboard</p>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={loadData} className="btn-primary">Try Again</button>
        </div>
      </div>
    );
  }

  const pl = stats?.partyLookups   || {};
  const q  = stats?.quotes         || {};
  const t  = stats?.transfers      || {};
  const fk = stats?.fspKeys        || {};
  const pr = stats?.payeeRequests  || {};

  const topCards = [
    {
      title:   'Party Lookups',
      total:   pl.total    || 0,
      ok:      pl.resolved || 0,
      pending: pl.pending  || 0,
      failed:  (pl.notFound || 0) + (pl.error || 0),
      icon:    Search,
      color:   'bg-blue-100 text-blue-600',
      href:    '/party-lookups',
    },
    {
      title:   'Quotes',
      total:   q.total    || 0,
      ok:      q.received || 0,
      pending: q.pending  || 0,
      failed:  (q.expired || 0) + (q.failed || 0),
      icon:    GitBranch,
      color:   'bg-success-100 text-success-600',
      href:    '/quotes',
    },
    {
      title:   'Transfers',
      total:   t.total     || 0,
      ok:      t.committed || 0,
      pending: (t.reserved || 0) + (t.pending || 0),
      failed:  (t.aborted  || 0) + (t.expired || 0) + (t.error || 0),
      icon:    ArrowRightLeft,
      color:   'bg-primary-100 text-primary-600',
      href:    '/transfers',
    },
    {
      title:   'Payee Requests',
      total:   pr.total     || 0,
      ok:      pr.responded || 0,
      pending: (pr.received || 0) + (pr.cbsNotified || 0),
      failed:  (pr.failed   || 0) + (pr.aborted     || 0),
      icon:    Inbox,
      color:   'bg-orange-100 text-orange-600',
      href:    '/payee-requests',
    },
    {
      title:   'FSP Keys',
      total:   fk.total  || 0,
      ok:      fk.active || 0,
      pending: 0,
      // Inactive keys were rotated or retired on purpose; they are history, not failures.
      failed:  0,
      icon:    Key,
      color:   'bg-purple-100 text-purple-600',
      href:    '/fsp-keys',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">Mojaloop FSPIOP real-time monitoring</p>
        </div>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-500">
            Last updated {formatRelativeTime(lastUpdate)}
          </span>
          <button
            onClick={loadData}
            disabled={loading}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Top stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {topCards.map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.title} className="stat-card">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg ${card.color}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <Link to={card.href} className="text-xs text-primary-600 hover:underline">
                  View all
                </Link>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">{card.title}</p>
                <p className="text-3xl font-bold text-gray-900">{formatCompactNumber(card.total)}</p>
                <div className="flex items-center gap-3 mt-2 text-xs">
                  <span className="text-success-600 font-medium">✓ {card.ok}</span>
                  {card.pending > 0 && <span className="text-warning-600">⏳ {card.pending}</span>}
                  {card.failed  > 0 && <span className="text-danger-600">✗ {card.failed}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Breakdown charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Transfer status breakdown */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Transfer Status</h2>
            <p className="text-sm text-gray-500">All recorded transfers</p>
          </div>
          <div className="card-body space-y-3">
            {stats ? (
              <>
                <BreakdownRow label="Committed" value={t.committed || 0} total={t.total || 0} colorClass="bg-success-500" />
                <BreakdownRow label="Reserved"  value={t.reserved  || 0} total={t.total || 0} colorClass="bg-blue-500" />
                <BreakdownRow label="Aborted"   value={t.aborted   || 0} total={t.total || 0} colorClass="bg-danger-500" />
                <BreakdownRow label="Expired"   value={t.expired   || 0} total={t.total || 0} colorClass="bg-warning-500" />
                <BreakdownRow label="Pending"   value={t.pending   || 0} total={t.total || 0} colorClass="bg-gray-300" />
                <BreakdownRow label="Error"     value={t.error     || 0} total={t.total || 0} colorClass="bg-danger-300" />
              </>
            ) : (
              <div className="h-48 flex items-center justify-center text-gray-500">No data</div>
            )}
          </div>
        </div>

        {/* Party lookup breakdown */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Party Lookup Outcomes</h2>
            <p className="text-sm text-gray-500">All recorded lookups</p>
          </div>
          <div className="card-body space-y-3">
            {stats ? (
              <>
                <BreakdownRow label="Resolved"  value={pl.resolved || 0} total={pl.total || 0} colorClass="bg-success-500" />
                <BreakdownRow label="Pending"   value={pl.pending  || 0} total={pl.total || 0} colorClass="bg-warning-500" />
                <BreakdownRow label="Not Found" value={pl.notFound || 0} total={pl.total || 0} colorClass="bg-gray-400" />
                <BreakdownRow label="Error"     value={pl.error    || 0} total={pl.total || 0} colorClass="bg-danger-500" />
              </>
            ) : (
              <div className="h-48 flex items-center justify-center text-gray-500">No data</div>
            )}
          </div>
        </div>

        {/* Payee request breakdown */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Payee Request Status</h2>
            <p className="text-sm text-gray-500">Inbound Hub-initiated requests</p>
          </div>
          <div className="card-body space-y-3">
            {stats ? (
              <>
                <BreakdownRow label="Responded"    value={pr.responded   || 0} total={pr.total || 0} colorClass="bg-success-500" />
                <BreakdownRow label="CBS Notified" value={pr.cbsNotified || 0} total={pr.total || 0} colorClass="bg-warning-500" />
                <BreakdownRow label="Received"     value={pr.received    || 0} total={pr.total || 0} colorClass="bg-blue-400" />
                <BreakdownRow label="Failed"       value={pr.failed      || 0} total={pr.total || 0} colorClass="bg-danger-500" />
                <BreakdownRow label="Aborted"      value={pr.aborted     || 0} total={pr.total || 0} colorClass="bg-gray-300" />
              </>
            ) : (
              <div className="h-48 flex items-center justify-center text-gray-500">No data</div>
            )}
          </div>
        </div>
      </div>

      {/* Quote pipeline */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Quote Pipeline</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { label: 'Total',    value: q.total    || 0, cls: 'text-gray-900'    },
              { label: 'Received', value: q.received || 0, cls: 'text-success-600' },
              { label: 'Pending',  value: q.pending  || 0, cls: 'text-warning-600' },
              { label: 'Expired',  value: q.expired  || 0, cls: 'text-orange-600'  },
              { label: 'Failed',   value: q.failed   || 0, cls: 'text-danger-600'  },
            ].map((item) => (
              <div key={item.label} className="text-center p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                <p className={`text-2xl font-bold ${item.cls}`}>{formatCompactNumber(item.value)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Transfers */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Recent Transfers</h2>
            <p className="text-sm text-gray-500">Latest 10</p>
          </div>
          <Link to="/transfers" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
            View All
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Transfer ID', 'Payer FSP', 'Payee FSP', 'Amount', 'Status', 'Created'].map((h) => (
                  <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(stats?.recentTransfers || []).length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    No transfers recorded yet
                  </td>
                </tr>
              ) : (
                (stats?.recentTransfers || []).map((tx) => (
                  <tr key={tx.id} className="hover:bg-gray-50">
                    <td className="px-6 py-3 whitespace-nowrap font-mono text-xs text-gray-900">
                    <Link to={`/transfers/${tx.id}`} className="text-primary-600 hover:underline">
                      {tx.transferId?.substring(0, 8)}…
                    </Link>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">{tx.payerFsp}</td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">{tx.payeeFsp}</td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {tx.amount}{' '}
                      <span className="text-gray-400 text-xs">{tx.currency}</span>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap">
                      <StatusPill status={tx.status} colorMap={TRANSFER_STATUS_COLORS} />
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                      {tx.createdAt ? formatRelativeTime(tx.createdAt) : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Payee Requests */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Recent Payee Requests</h2>
            <p className="text-sm text-gray-500">Latest 5 inbound Hub requests</p>
          </div>
          <Link to="/payee-requests" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
            View All
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Type', 'Resource ID', 'Payer FSP', 'Payee FSP', 'Status', 'Received'].map((h) => (
                  <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(stats?.recentPayeeRequests || []).length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    No payee requests recorded yet
                  </td>
                </tr>
              ) : (
                (stats?.recentPayeeRequests || []).map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-6 py-3 whitespace-nowrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 font-mono">
                        {REQUEST_TYPE_LABELS[r.requestType] ?? r.requestType}
                      </span>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap font-mono text-xs text-gray-900">
                      <Link to="/payee-requests" className="text-primary-600 hover:underline">
                        {r.resourceId?.length > 16 ? r.resourceId.substring(0, 8) + '…' : r.resourceId}
                      </Link>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">{r.fspiopSource ?? '—'}</td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">{r.fspiopDest  ?? '—'}</td>
                    <td className="px-6 py-3 whitespace-nowrap">
                      <StatusPill status={r.status} colorMap={PAYEE_STATUS_COLORS} />
                      {r.errorCode && (
                        <span className="ml-1 text-xs text-danger-600 font-mono">{r.errorCode}</span>
                      )}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                      {r.receivedAt ? formatRelativeTime(r.receivedAt) : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
